import { useEffect, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { loadFromLocal, saveToLocal } from '../lib/utils'
import {
  PENDING_SCORES_STORAGE_KEY,
  buildPendingScorePayload,
  recordPendingScoreFailure,
} from '../lib/pendingScoresQueue'

const STORAGE_KEY = PENDING_SCORES_STORAGE_KEY

/**
 * Best-effort background sync for scores saved to localStorage while the team
 * was offline.
 *
 * Play.jsx writes an entry to `puttzin_pending_scores_v2` _before_ attempting the Supabase
 * RPC so a dropped request never loses the hole. Historically those entries
 * only got retried the next time a player re-submitted scores on that hole,
 * which effectively means they never sync on their own.
 *
 * This hook wires two cheap retry triggers:
 *   * `online` event — browser transitioned from offline to online
 *   * `visibilitychange` — tab came back to foreground (covers iOS Safari where
 *     the `online` event is unreliable when returning from lockscreen)
 *
 * Both paths read the queue, call `upsert_team_scores_for_hole` for each hole,
 * and remove entries that succeed. Entries that keep failing (bad PIN, hole
 * already approved by staff, etc.) drop after MAX_ATTEMPTS so we never get
 * stuck in an infinite retry loop.
 */
async function flushOnce() {
  const queue = loadFromLocal(STORAGE_KEY)
  if (!Array.isArray(queue) || queue.length === 0) return 0

  const team = loadFromLocal('currentTeam')
  if (!team?.id || !team?.pin) return 0

  const remaining = []
  let synced = 0

  for (const entry of queue) {
    if (!entry || entry.team_id !== team.id) {
      remaining.push(entry)
      continue
    }

    const pScores = buildPendingScorePayload(entry)

    try {
      const { data, error } = await supabase.rpc('upsert_team_scores_for_hole', {
        p_team_id: entry.team_id,
        p_pin: team.pin,
        p_hole_id: entry.hole_id,
        p_scores: pScores,
      })

      const rpcFailed = Boolean(error) || (data && data.success === false)
      if (rpcFailed) {
        const nextEntry = recordPendingScoreFailure(entry)
        if (!nextEntry) {
          console.warn('[pendingScoresSync] dropping entry after max attempts', entry, data || error)
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('pending-scores-sync-failed', {
              detail: { holeId: entry.hole_id },
            }))
          }
          continue
        }
        remaining.push(nextEntry)
      } else {
        synced += 1
      }
    } catch (err) {
      console.warn('[pendingScoresSync] network error, will retry later', err)
      remaining.push(entry)
    }
  }

  if (remaining.length === 0) {
    saveToLocal(STORAGE_KEY, null)
  } else {
    saveToLocal(STORAGE_KEY, remaining)
  }
  return synced
}

export default function usePendingScoresSync() {
  const runningRef = useRef(false)

  useEffect(() => {
    let cancelled = false

    const run = async (reason) => {
      if (runningRef.current) return
      runningRef.current = true
      try {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return
        const synced = await flushOnce()
        if (!cancelled && synced > 0) {
          console.log(`[pendingScoresSync] flushed ${synced} pending hole(s) (${reason})`)
        }
      } finally {
        runningRef.current = false
      }
    }

    // Kick one on mount in case we loaded while offline saves were pending.
    run('mount')

    const onOnline = () => run('online')
    const onVisible = () => {
      if (document.visibilityState === 'visible') run('visible')
    }

    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
}
